import { FC } from 'react';

import { BaseCheckableElement, HighlighterProps } from './CheckableElement';
import CanvasBasedHighlighter from '../components/Highlighter/CanvasBased';
import { GrammarCheckAlert } from '../types';

const HIDDEN_DIV_SYNCED_STYLES = [
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'font-variant',
  'font-stretch',
  'line-height',
  'letter-spacing',
  'text-align',
  'direction',
  'white-space',
  'word-break',
  'word-wrap',
  'overflow-wrap',
  'tab-size',
  'box-sizing',
  'padding-top',
  'padding-right',
  'padding-bottom',
  'padding-left',
  'border-top-width',
  'border-right-width',
  'border-bottom-width',
  'border-left-width',
  'border-top-style',
  'border-right-style',
  'border-bottom-style',
  'border-left-style',
  'border-top-left-radius',
  'border-top-right-radius',
  'border-bottom-right-radius',
  'border-bottom-left-radius',
  'width',
  'height',
];

export class TextareaCheckableElement extends BaseCheckableElement {
  private hiddenDiv!: HTMLDivElement;
  private hiddenDivStylesUpdateInterval: ReturnType<typeof setInterval> | null =
    null;

  constructor(element: HTMLElement) {
    super(element);
    this.setupHiddenDiv();
  }

  calculateAlertClientRects(alerts: GrammarCheckAlert[]): DOMRect[][] {
    const rects: DOMRect[][] = [];
    for (const alert of alerts) {
      rects.push(this.calculateAlertClientRect(alert));
    }
    return rects;
  }

  private calculateAlertClientRect(alert: GrammarCheckAlert): DOMRect[] {
    const textArea = this.element as HTMLTextAreaElement;
    const { absOffset, length } = alert;
    const textContent = this.getText();
    const textBeforeAlert = textContent.substring(0, absOffset);
    const alertText = textContent.substring(absOffset, absOffset + length);
    const textAfterAlert = textContent.substring(absOffset + length);

    this.updateHiddenDivPosition();

    this.hiddenDiv.innerHTML = this.textToHTML(
      textBeforeAlert,
      alertText,
      textAfterAlert,
    );

    const rects: DOMRect[] = [];

    const startSpan = this.hiddenDiv.querySelector('#start-marker');
    const endSpan = this.hiddenDiv.querySelector('#end-marker');

    if (startSpan && endSpan) {
      const range = document.createRange();
      range.setStartBefore(startSpan);
      range.setEndAfter(endSpan);

      const rectsForRange = range.getClientRects();

      for (const rect of rectsForRange) {
        const textAreaRect = textArea.getBoundingClientRect();
        rects.push(
          new DOMRect(
            rect.left - textAreaRect.left,
            rect.top - textAreaRect.top - textArea.scrollTop,
            rect.width,
            rect.height,
          ),
        );
      }
    } else {
      console.warn('Start or end marker not found in hidden div.');
      return [new DOMRect(0, 0, 0, 0)];
    }

    return rects;
  }

  insertText(text: string): void {
    const textArea = this.element as HTMLTextAreaElement;
    textArea.value = text;
    this.dispatchInputEvent();
  }

  private dispatchInputEvent(): void {
    const inputEvent = new Event('input', { bubbles: true });
    this.element.dispatchEvent(inputEvent);
  }

  async replaceText(alert: GrammarCheckAlert): Promise<void> {
    const textArea = this.element as HTMLTextAreaElement;
    const { absOffset, length } = alert;
    const before = textArea.value.substring(0, absOffset);
    const after = textArea.value.substring(absOffset + length);
    textArea.value = before + alert.replacements[0] + after;
    this.dispatchInputEvent();
  }

  async replaceAllText(alerts: GrammarCheckAlert[]): Promise<void> {
    const textArea = this.element as HTMLTextAreaElement;
    const preparedAlerts = this.adjustAlerts(alerts);
    let tempValue = textArea.value;
    preparedAlerts.forEach((alert) => {
      const { absOffset, length } = alert;
      const before = tempValue.substring(0, absOffset);
      const after = tempValue.substring(absOffset + length);
      tempValue = before + alert.replacements[0] + after;
    });
    textArea.value = tempValue;
    this.dispatchInputEvent();
  }

  getText(): string {
    return (this.element as HTMLTextAreaElement).value;
  }

  destroy(): void {
    return this.destroyHiddenDiv();
  }

  private setupHiddenDiv(): void {
    this.hiddenDiv = document.createElement('div');
    this.updateHiddenDivStyles();
    document.body.appendChild(this.hiddenDiv);
    this.hiddenDivStylesUpdateInterval = setInterval(() => {
      this.updateHiddenDivStyles();
    }, 500);
  }

  private destroyHiddenDiv(): void {
    if (!this.hiddenDiv || !this.hiddenDiv.parentNode) {
      return;
    }
    document.body.removeChild(this.hiddenDiv);
    if (this.hiddenDivStylesUpdateInterval) {
      clearInterval(this.hiddenDivStylesUpdateInterval);
    }
  }

  private updateHiddenDivPosition(): void {
    const rect = this.element.getBoundingClientRect();
    this.hiddenDiv.style.top = `${rect.top + window.scrollY}px`;
    this.hiddenDiv.style.left = `${rect.left + window.scrollX}px`;
    this.hiddenDiv.style.width = `${rect.width}px`;
    this.hiddenDiv.style.height = `${rect.height}px`;
    this.hiddenDiv.style.display = 'block';
  }

  private textToHTML(
    beforeText: string,
    alertText: string,
    afterText: string,
  ): string {
    const escapeHTML = (text: string) =>
      text.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return `${escapeHTML(
      beforeText,
    )}\u200b<span id="start-marker"></span>${escapeHTML(
      alertText,
    )}<span id="end-marker"></span>${escapeHTML(afterText)}`;
  }

  private updateHiddenDivStyles(): void {
    const styles = window.getComputedStyle(this.element);
    HIDDEN_DIV_SYNCED_STYLES.forEach((styleName) => {
      this.hiddenDiv.style.setProperty(
        styleName,
        styles.getPropertyValue(styleName),
      );
    });
    this.hiddenDiv.style.position = 'absolute';
    this.hiddenDiv.style.visibility = 'hidden';
    this.hiddenDiv.style.whiteSpace = 'pre-wrap';
    this.hiddenDiv.style.wordWrap = 'break-word';
    this.hiddenDiv.style.overflow = 'auto';
    this.hiddenDiv.style.height = 'auto';
    this.hiddenDiv.style.display = 'none';
    this.hiddenDiv.id = 'justdone-grammar-hidden-div';
  }

  getHighlighter(): FC<HighlighterProps> {
    return CanvasBasedHighlighter;
  }
}
